import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './StanzaCard.css';
import { parseDialogueText } from '../../utils/dialogueFormatter';
import { logEngagementEvent } from '../../services/engagementTrackingService.js';
import { orderMixpanelProperties } from '../../utils/mixpanelPropertyOrder.js';

// ============================================================================
// PROP DEFAULTS
// ============================================================================
const DEFAULT_PROPS = {
  number: '1',
  chapterIndex: null,
  chapterTotal: null,
  bookId: null,
  bookTitle: null,
  chapterNum: null,
  highlightEntries: [],
  onHighlightCreated: () => {},
  highlightActiveStanza: null,
  onHighlightModeChange: () => {},
  text: '',
  originalText: '',
  adaptedText: '',
  onContentSwitch: () => {},
  hasImage: false,
  imageUrl: '',
  imageDescription: '',
  bookType: null,
  userLevel: 'A1',
  initialIsShowingAdapted: true,
  showActions: true,
  showImageToggle: true,
  showContentToggle: true,
  showLikeDislike: true,
  showHighlightToggle: true,
};

const HIGHLIGHT_CACHE_KEY = 'highlights_cache_v1';
const MAX_HIGHLIGHT_LENGTH = 1000;

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const StanzaCard = ({
  number = DEFAULT_PROPS.number,
  chapterIndex = DEFAULT_PROPS.chapterIndex,
  chapterTotal = DEFAULT_PROPS.chapterTotal,
  bookId = DEFAULT_PROPS.bookId,
  bookTitle = DEFAULT_PROPS.bookTitle,
  chapterNum = DEFAULT_PROPS.chapterNum,
  highlightEntries = DEFAULT_PROPS.highlightEntries,
  onHighlightCreated = DEFAULT_PROPS.onHighlightCreated,
  highlightActiveStanza = DEFAULT_PROPS.highlightActiveStanza,
  onHighlightModeChange = DEFAULT_PROPS.onHighlightModeChange,
  text = DEFAULT_PROPS.text,
  originalText = DEFAULT_PROPS.originalText,
  adaptedText = DEFAULT_PROPS.adaptedText,
  onContentSwitch = DEFAULT_PROPS.onContentSwitch,
  hasImage = DEFAULT_PROPS.hasImage,
  imageUrl = DEFAULT_PROPS.imageUrl,
  imageDescription = DEFAULT_PROPS.imageDescription,
  bookType = DEFAULT_PROPS.bookType,
  userLevel = DEFAULT_PROPS.userLevel,
  initialIsShowingAdapted = DEFAULT_PROPS.initialIsShowingAdapted,
  showActions = DEFAULT_PROPS.showActions,
  showImageToggle = DEFAULT_PROPS.showImageToggle,
  showContentToggle = DEFAULT_PROPS.showContentToggle,
  showLikeDislike = DEFAULT_PROPS.showLikeDislike,
  showHighlightToggle = DEFAULT_PROPS.showHighlightToggle,
}) => {
  // ========================================================================
  // STATE MANAGEMENT
  // ========================================================================
  const [clickedStates, setClickedStates] = useState({
    like: false,
    dislike: false,
    reload: false,
    image: false,
    highlight: false,
  });

  const [isShowingImage, setIsShowingImage] = useState(false);
  const [isHighlightBlocked, setIsHighlightBlocked] = useState(false);
  const [isContentSwitching, setIsContentSwitching] = useState(false);
  const stanzaCardRef = useRef(null);
  const stanzaTextRef = useRef(null);
  const pendingViewportTopRef = useRef(null);
  const positionFrameRef = useRef(null);

  const preserveCardPosition = () => {
    if (pendingViewportTopRef.current !== null || !stanzaCardRef.current) {
      return;
    }

    pendingViewportTopRef.current = stanzaCardRef.current.getBoundingClientRect().top;
  };

  useLayoutEffect(() => {
    if (pendingViewportTopRef.current === null || !stanzaCardRef.current) {
      return undefined;
    }

    positionFrameRef.current = window.requestAnimationFrame(() => {
      if (!stanzaCardRef.current || pendingViewportTopRef.current === null) {
        return;
      }

      const currentTop = stanzaCardRef.current.getBoundingClientRect().top;
      const positionDelta = currentTop - pendingViewportTopRef.current;

      if (Math.abs(positionDelta) > 0.5) {
        window.scrollBy(0, positionDelta);
      }

      pendingViewportTopRef.current = null;
      positionFrameRef.current = null;
    });

    return () => {
      if (positionFrameRef.current !== null) {
        window.cancelAnimationFrame(positionFrameRef.current);
        positionFrameRef.current = null;
      }
    };
  });

  // ========================================================================
  // ANALYTICS & TRACKING
  // ========================================================================
  const getMixpanelBaseProperties = () => ({
    stanza_number: number,
    chapter_num: chapterNum,
    book_id: bookId || 'unknown',
    book_title: bookTitle || 'unknown',
    event_timestamp: Date.now(),
    user_id: localStorage.getItem('auth_id') || 'anonymous',
    cefr_level: localStorage.getItem('cefr_level') || 'unknown',
  });

  const trackMixpanelEvent = (eventName, properties = {}) => {
    const fullProperties = orderMixpanelProperties({ ...getMixpanelBaseProperties(), ...properties });
    void logEngagementEvent(eventName, fullProperties);

    if (window.mixpanel) {
      window.mixpanel.track(eventName, fullProperties);
    }
    console.log(`🔍 Mixpanel Event: ${eventName}`, fullProperties);
  };

  // ========================================================================
  // ACTION HANDLERS - LIKE/DISLIKE
  // ========================================================================
  const handleLikeClick = () => {
    if (!showLikeDislike) return;

    preserveCardPosition();
    const newStates = { ...clickedStates };
    if (clickedStates.dislike) {
      newStates.like = true;
      newStates.dislike = false;
    } else {
      newStates.like = !clickedStates.like;
    }
    setClickedStates(newStates);

    if (!clickedStates.like) {
      trackMixpanelEvent('like_button_clicked', {
        action: 'like',
        content_type: initialIsShowingAdapted ? 'adapted' : 'original',
        has_image: hasImage,
      });
    }
  };

  const handleDislikeClick = () => {
    if (!showLikeDislike) return;

    preserveCardPosition();
    const newStates = { ...clickedStates };
    if (clickedStates.like) {
      newStates.like = false;
      newStates.dislike = true;
    } else {
      newStates.dislike = !clickedStates.dislike;
    }
    setClickedStates(newStates);

    if (!clickedStates.dislike) {
      trackMixpanelEvent('dislike_button_clicked', {
        action: 'dislike',
        content_type: initialIsShowingAdapted ? 'adapted' : 'original',
        has_image: hasImage,
      });
    }
  };

  // ========================================================================
  // ACTION HANDLERS - CONTENT & IMAGE
  // ========================================================================
  const handleContentSwitch = () => {
    if (!showContentToggle) return;

    preserveCardPosition();
    setClickedStates(prev => ({
      ...prev,
      reload: !prev.reload,
    }));
    setIsContentSwitching(true);
    window.setTimeout(() => setIsContentSwitching(false), 220);

    const newState = !initialIsShowingAdapted;
    trackMixpanelEvent('content_switch_clicked', {
      action: 'reload',
      from_content_type: initialIsShowingAdapted ? 'adapted' : 'original',
      to_content_type: newState ? 'adapted' : 'original',
    });

    onContentSwitch?.(number, newState);
  };

  const handleImageToggle = () => {
    if (!showImageToggle || !hasImage) return;

    // Preserve the card position while the side-image layout expands or collapses.
    preserveCardPosition();
    setClickedStates(prev => ({
      ...prev,
      image: !prev.image,
    }));

    const newImageState = !isShowingImage;
    trackMixpanelEvent('image_toggle_clicked', {
      action: 'image',
      image_action: newImageState ? 'show' : 'hide',
      image_url: imageUrl,
      has_image_description: !!imageDescription,
    });

    setIsShowingImage(newImageState);
  };

  const handleHighlightToggle = () => {
    if (!showHighlightToggle || !initialIsShowingAdapted || isHighlightLocked) return;

    preserveCardPosition();
    const nextHighlightState = !clickedStates.highlight;
    setClickedStates(prev => ({
      ...prev,
      highlight: nextHighlightState,
    }));

    onHighlightModeChange(number, nextHighlightState);

    if (!nextHighlightState) {
      setIsHighlightBlocked(false);
      return;
    }

    trackMixpanelEvent('highlight_toggle_clicked', {
      action: 'highlight',
      highlight_enabled: !clickedStates.highlight,
    });
  };

  // ========================================================================
  // CONTENT DETECTION & FORMATTING
  // ========================================================================
  const displayText = text || (initialIsShowingAdapted ? adaptedText : originalText);

  const detectDialogueFormatting = (textContent) => {
    return (
      textContent &&
      textContent.includes(':') &&
      textContent.split('\n').some(line => {
        const trimmed = line.trim();
        return trimmed.endsWith(':') && trimmed === trimmed.toUpperCase() && trimmed.length > 1;
      })
    );
  };

  const hasDialogueFormatting = detectDialogueFormatting(displayText);
  const dialogueElements = hasDialogueFormatting ? parseDialogueText(displayText) : null;
  const hasOriginalContent = Boolean(originalText && originalText.trim());
  const hasContent = displayText || originalText || adaptedText;
  const showChapterLabel = Number.isFinite(chapterIndex) && Number.isFinite(chapterTotal);
  const chapterLabel = showChapterLabel ? `${chapterIndex}/${chapterTotal}` : null;
  const isHighlighting = clickedStates.highlight;
  const canHighlight = showHighlightToggle && initialIsShowingAdapted;
  const isHighlightLocked =
    highlightActiveStanza !== null &&
    String(highlightActiveStanza) !== String(number);

  useEffect(() => {
    if (initialIsShowingAdapted) return;
    if (clickedStates.highlight) {
      setClickedStates(prev => ({ ...prev, highlight: false }));
      onHighlightModeChange(number, false);
    }
    setIsHighlightBlocked(false);
  }, [initialIsShowingAdapted, clickedStates.highlight, number, onHighlightModeChange]);

  const getHighlightCache = () => {
    try {
      const raw = localStorage.getItem(HIGHLIGHT_CACHE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      return [];
    }
  };

  const writeHighlightCache = (entries) => {
    try {
      localStorage.setItem(HIGHLIGHT_CACHE_KEY, JSON.stringify(entries));
    } catch (error) {
    }
  };

  const buildHighlightKey = (entry) => (
    [
      entry.user_id,
      entry.book_id,
      entry.chapter_num,
      entry.stanza_num,
      entry.start_idx,
      entry.end_idx,
    ].join('|')
  );

  const addHighlightToCache = (entry) => {
    const cache = getHighlightCache();
    const existingKeys = new Set(cache.map(buildHighlightKey));
    const entryKey = buildHighlightKey(entry);

    if (existingKeys.has(entryKey)) {
      return;
    }

    cache.push(entry);
    writeHighlightCache(cache);
  };

  const wrapTextRange = (container, startIdx, endIdx, entryKey) => {
    if (startIdx >= endIdx) return false;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];
    let currentOffset = 0;
    let node = walker.nextNode();

    while (node) {
      const nodeEnd = currentOffset + node.textContent.length;
      const overlapStart = Math.max(startIdx, currentOffset);
      const overlapEnd = Math.min(endIdx, nodeEnd);

      if (overlapStart < overlapEnd) {
        textNodes.push({
          node,
          start: overlapStart - currentOffset,
          end: overlapEnd - currentOffset,
        });
      }

      currentOffset = nodeEnd;
      node = walker.nextNode();
    }

    // Mutate from the end so splitting an earlier node does not affect later offsets.
    textNodes.reverse().forEach(({ node: textNode, start, end }) => {
      if (end < textNode.textContent.length) {
        textNode.splitText(end);
      }
      const highlightedNode = start > 0 ? textNode.splitText(start) : textNode;

      const span = document.createElement('span');
      span.className = 'highlighted-text';
      span.dataset.highlightKey = entryKey;
      highlightedNode.parentNode.insertBefore(span, highlightedNode);
      span.appendChild(highlightedNode);
    });

    return textNodes.length > 0;
  };

  const getTextOffset = (container, boundaryContainer, boundaryOffset) => {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    let currentOffset = 0;
    let node = walker.nextNode();

    while (node) {
      if (node === boundaryContainer) {
        return currentOffset + boundaryOffset;
      }

      const nodeRange = document.createRange();
      nodeRange.selectNodeContents(node);
      if (nodeRange.comparePoint(boundaryContainer, boundaryOffset) > 0) {
        currentOffset += node.textContent.length;
        node = walker.nextNode();
        continue;
      }

      return currentOffset;
    }

    return currentOffset;
  };

  const applyHighlightEntry = (entry) => {
    const container = stanzaTextRef.current;
    if (!container) return;

    const entryKey = buildHighlightKey(entry);
    if (Array.from(container.querySelectorAll('.highlighted-text'))
      .some(element => element.dataset.highlightKey === entryKey)) {
      return;
    }

    wrapTextRange(container, entry.start_idx, entry.end_idx, entryKey);
  };

  const applyHighlightEntries = (entries) => {
    if (!entries || entries.length === 0) return;
    entries.forEach(applyHighlightEntry);
  };

  const applyHighlightFromSelection = () => {
    if (!isHighlighting || !canHighlight || isHighlightBlocked || isHighlightLocked) {
      return;
    }

    const container = stanzaTextRef.current;
    if (!container) return;

    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;

    const range = selection.getRangeAt(0);
    const isSelectionWithin =
      container.contains(range.startContainer) &&
      container.contains(range.endContainer);

    if (!isSelectionWithin) {
      setIsHighlightBlocked(true);
      selection.removeAllRanges();
      return;
    }

    const selectedText = selection.toString();
    if (!selectedText) return;

    const containerText = container.textContent || '';
    if (
      selectedText.length > MAX_HIGHLIGHT_LENGTH &&
      selectedText.length < containerText.length
    ) {
      selection.removeAllRanges();
      return;
    }

    // Validate required metadata before creating highlight
    if (!bookId || !Number.isFinite(chapterNum) || chapterNum <= 0) {
      selection.removeAllRanges();
      return;
    }

    const startIdx = getTextOffset(container, range.startContainer, range.startOffset);
    const endIdx = getTextOffset(container, range.endContainer, range.endOffset);

    const userId = localStorage.getItem('auth_id') || 'anonymous';
    const highlightEntry = {
      user_id: userId,
      book_id: bookId,
      chapter_num: chapterNum,
      stanza_num: Number.isFinite(Number(number)) ? Number(number) : number,
      start_idx: startIdx,
      end_idx: endIdx,
      quote: selectedText,
    };

    const entryKey = buildHighlightKey(highlightEntry);

    wrapTextRange(container, startIdx, endIdx, entryKey);

    selection.removeAllRanges();

    addHighlightToCache(highlightEntry);
    onHighlightCreated(highlightEntry);
  };

  useEffect(() => {
    if (!initialIsShowingAdapted) return;
    applyHighlightEntries(highlightEntries);
  });

  // ========================================================================
  // RENDER - PLACEHOLDER
  // ========================================================================
  if (!hasContent) {
    return (
      <div className="stanza-card placeholder">
        {showChapterLabel && (
          <div className="chapter-card-number">{chapterLabel}</div>
        )}
        <div className="stanza-number">{number}:</div>
        <div className="stanza-text placeholder-text">
          <em>Content for stanza {number} will be available soon...</em>
        </div>
      </div>
    );
  }

  if (!initialIsShowingAdapted && !hasOriginalContent) {
    return (
      <div className="stanza-card placeholder">
        {showChapterLabel && (
          <div className="chapter-card-number">{chapterLabel}</div>
        )}
        <div className="stanza-number">{number}:</div>
        <div className="stanza-text placeholder-text">
          <em>Original content unavailable for this stanza.</em>
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER - CONTENT TYPE TAG
  // ========================================================================
  const ContentTypeTag = () => (
    <div className="content-type-tag">
      {initialIsShowingAdapted
        ? 'AI Adapted Content'
        : (bookType === 'textbook' ? 'Original Traditional Chinese' : 'Original Gutenberg Text')}
    </div>
  );

  // ========================================================================
  // TOUCH EVENT HANDLERS
  // ========================================================================
  const handleTouchStart = (e) => {
    if (!isHighlighting) return;
    
    // Ignore multi-touch gestures (pinch, etc.)
    if (e.touches.length > 1) {
      setIsHighlightBlocked(true);
      return;
    }

    // Store touch state for validation on touchend
    // This prevents accidental highlights from scroll gestures
    const target = e.target;
    if (target && stanzaTextRef.current && stanzaTextRef.current.contains(target)) {
      // Allow selection to proceed
      return;
    }
  };

  const handleTouchEnd = (e) => {
    if (!isHighlighting) return;
    
    // Only process if touch was within the stanza text container
    if (!stanzaTextRef.current) return;

    const selection = window.getSelection();
    
    // Only trigger highlight if there's an actual selection
    if (selection && !selection.isCollapsed) {
      // Small delay to ensure selection is stabilized on mobile
      setTimeout(() => {
        applyHighlightFromSelection();
      }, 120);
    }
  };

  const handlePointerUp = (e) => {
    if (!isHighlighting) return;

    // Chrome device emulation and many mobile browsers dispatch pointer events.
    if (e.pointerType !== 'touch' && e.pointerType !== 'pen') {
      return;
    }

    setTimeout(() => {
      applyHighlightFromSelection();
    }, 120);
  };

  const handleTouchCancel = () => {
    setIsHighlightBlocked(false);
  };

  // ========================================================================
  // RENDER - STANZA TEXT
  // ========================================================================
  const StanzaText = () => (
    <div
      className="stanza-text"
      ref={stanzaTextRef}
      onMouseUp={applyHighlightFromSelection}
      onPointerUp={handlePointerUp}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
    >
      {hasDialogueFormatting ? (
        dialogueElements.map((element, index) => (
          <div key={index} className={`dialogue-element dialogue-${element.type}`}>
            {element.type === 'character' && (
              <div className="character-name">{element.content}</div>
            )}
            {element.type === 'dialogue' && (
              <div className="dialogue-text">{element.content}</div>
            )}
            {element.type === 'break' && (
              <div className="dialogue-break" />
            )}
          </div>
        ))
      ) : (
        <div dangerouslySetInnerHTML={{ __html: displayText }} />
      )}
    </div>
  );

  // ========================================================================
  // RENDER - IMAGE DISPLAY
  // ========================================================================
  const imageDisplay = hasImage && isShowingImage ? (() => {
    const encodedImageUrl = imageUrl.replace(/#/g, '%23').replace(/,/g, '%2C');

    return (
      <div className="stanza-image-container">
        <img
          src={encodedImageUrl}
          alt={`Illustration for stanza ${number}`}
          className="stanza-image"
          onError={(e) => {
          }}
        />
        {imageDescription && (
          <div className="image-caption">{imageDescription}</div>
        )}
      </div>
    );
  })() : null;

  // ========================================================================
  // RENDER - ACTION BUTTONS
  // ========================================================================
  const ActionButtons = () => {
    if (!showActions) return null;

    return (
      <div className="stanza-actions">
        {/* Like Button */}
        {showLikeDislike && (
          <button
            className={`action-btn ${clickedStates.like ? 'clicked' : ''}`}
            title="Like"
            onClick={handleLikeClick}
            aria-label="Like this stanza"
            disabled={isHighlightLocked}
          >
            <i className="fas fa-thumbs-up" />
          </button>
        )}

        {/* Dislike Button */}
        {showLikeDislike && (
          <button
            className={`action-btn ${clickedStates.dislike ? 'clicked' : ''}`}
            title="Dislike"
            onClick={handleDislikeClick}
            aria-label="Dislike this stanza"
            disabled={isHighlightLocked}
          >
            <i className="fas fa-thumbs-down" />
          </button>
        )}

        {/* Content Switch Button */}
        {showContentToggle && (
          <button
            className={`action-btn ${clickedStates.reload ? 'clicked' : ''}`}
            title="Switch Content Type"
            onClick={handleContentSwitch}
            aria-label="Switch between adapted and original content"
            disabled={isHighlightLocked}
          >
             <i className="fa-solid fa-right-left" />
          </button>
        )}

        {/* Image Toggle Button */}
        {showImageToggle && (
          <button
            className={`action-btn ${clickedStates.image ? 'clicked' : ''} ${
              !hasImage ? 'disabled' : ''
            }`}
            title={hasImage ? 'Show Image' : 'No image available'}
            onClick={handleImageToggle}
            disabled={!hasImage || isHighlightLocked}
            aria-label={hasImage ? 'Toggle image display' : 'No image available'}
          >
            <i className="fas fa-image" />
          </button>
        )}

        {/* Highlight Button */}
        {showHighlightToggle && (
          <button
            className={`action-btn highlight-btn ${clickedStates.highlight ? 'clicked' : ''}`}
            title="Highlight"
            onClick={handleHighlightToggle}
            aria-label="Toggle highlight mode"
            disabled={!initialIsShowingAdapted || isHighlightLocked}
          >
            <i className="fas fa-highlighter" />
          </button>
        )}
      </div>
    );
  };

  // ========================================================================
  // MAIN RENDER
  // ========================================================================
  return (
    <div
      ref={stanzaCardRef}
      className={`stanza-card ${hasImage ? 'has-side-image' : ''} ${
        !initialIsShowingAdapted ? 'showing-original' : ''
      } ${isHighlighting ? 'highlight-mode' : ''} ${
        isHighlightBlocked ? 'highlight-blocked' : ''
      } ${isHighlightLocked ? 'highlight-locked' : ''} ${
        isContentSwitching ? 'content-switching' : ''
      } ${hasImage && isShowingImage ? 'image-side-layout' : ''}`}
    >
      <ContentTypeTag />
      {showChapterLabel && (
        <div className="chapter-card-number">{chapterLabel}</div>
      )}
      <div className="stanza-number">{number}:</div>
      <StanzaText />
      {imageDisplay}
      <ActionButtons />
    </div>
  );
};

export default StanzaCard;
